/**
 * @fileoverview Bankruptcy case header chips (MOR-1260)
 * @module components/jobs/phases/review/flaggedTransactions/BankruptcyCaseChips
 *
 * Commencement of bankruptcy (with its s 115 pathway), date of bankruptcy and
 * petition type: the anchors the Bankruptcy Act heads run from. Replaces the
 * Corporations Act framing on a personal job; the insolvency date chip stays
 * beside these because s 122 still needs it.
 */

import type { ReactNode } from "react";
import { CalendarClock, Gavel } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import { fmtDate, agentText, type BankruptcyCaseUI } from "./flaggedShared";

function Chip({
  icon,
  label,
  value,
  detail,
  title,
}: {
  icon?: ReactNode;
  label: string;
  value: string;
  detail?: string | null;
  title?: string;
}) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1.5 text-xs"
      title={title}
    >
      {icon}
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium tabular-nums">{value}</span>
      {detail && (
        <span className="text-[10px] text-muted-foreground">({detail})</span>
      )}
    </div>
  );
}

export function BankruptcyCaseChips({
  bankruptcy,
}: {
  bankruptcy: BankruptcyCaseUI;
}) {
  return (
    <>
      <Chip
        icon={<Gavel className="h-3.5 w-3.5 text-muted-foreground" />}
        label="Commencement of bankruptcy"
        value={
          bankruptcy.commencement
            ? fmtDate(bankruptcy.commencement)
            : "Not yet available"
        }
        detail={bankruptcy.section}
        title={agentText(bankruptcy.note)}
      />
      <Chip
        icon={<CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />}
        label="Date of bankruptcy"
        value={
          bankruptcy.dateOfBankruptcy
            ? fmtDate(bankruptcy.dateOfBankruptcy)
            : "Not recorded"
        }
      />
      <Chip
        label="Petition type"
        value={bankruptcy.petitionTypeLabel}
        detail={
          bankruptcy.petitionPresentedDate
            ? `presented ${fmtDate(bankruptcy.petitionPresentedDate)}`
            : null
        }
      />
      {bankruptcy.legacy && (
        <Badge color="amber" variant="soft" size="sm">
          Petition type not recorded
        </Badge>
      )}
    </>
  );
}
